import { takeEvery, put, call, select } from "redux-saga/effects"
import { saveAs } from "file-saver"

// Types
import CaseTypes from "Containers/MSF/Cases/types"
import DocuSignTypes from "../DocuSign/types"

// Actions
import { setErrorMessage, setInfoMessage } from "Containers/Messages/actions"
import {
  createAffaireSuccess,
  getAffairesSuccess,
  getAffaireByIdSuccess,
  updateDataAffaireSuccess,
  getNumberOfAffairesCreatedThisYearSuccess,
  getLineNameByCodeSuccess,
  updateAffaireSuccess,
  getModelsSuccess,
  getWorksPlanningSuccess,
  getCapacitesSuccess,
  createModelSuccess,
  deleteModelSuccess,
} from "Containers/MSF/Cases/actions"
import {
  getDocuSignAuthUrlSuccess,
  getDocuSignAccessTokenSuccess,
} from "Containers/MSF/DocuSign/actions"
import { postNotificationAction } from "Containers/Notifications/actions"
import { setSuccessCode } from "Containers/Messages/actions"

// Selectors
import { selectCurrentToken, selectLangageID } from "Containers/Login/selectors"

// Saga
import {
  responseTypeIsSuccess,
  responseTypeIsError,
  manageSuccessGenerator,
  manageErrorGenerator,
  handleResp,
} from "Containers/App/saga"

// Utils
import request, { requestFile, etlRequest } from "Utils/request"
import { postFileCors, getCors, postCors, deleteCors, patchCors } from "Utils/rulesCors"
import { getStringRemoveSpecialCharacters } from "Utils/global"
import { getTranslationString } from "Utils/getTranslation"
import { config } from "Utils/config"

export function* createAffaireSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  const { body, files, notification, infos_notif } = payload

  try {
    let filesNanoid = []
    if (files?.length) {
      const filesLoop = yield call(processLoop, files)
      if (!filesLoop) return
      filesNanoid = filesLoop?.map(file => file?.fileId)
    }
    const url = `${config?.MSF_BACK_URL}/affaires`
    const payloadToSend = {
      ...body,
      [payload?.fileField]: filesNanoid,
    }
    const response = yield call(request, url, postCors(payloadToSend, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)

    if (responseTypeIsError(response)) return
    yield put(createAffaireSuccess(response))
    yield put(setSuccessCode("SUCCESS_TO_CREATE_AFFAIRE"))

    if (notification) {
      yield put(postNotificationAction({ notif: notification, infos_notif }))
    }
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

/**
 * Etape 2 Boucle files
 * @param {*} param0
 */
function* processLoop(files) {
  let result = []

  for (let file of files) {
    const responsePostFile = yield call(postFile, file)
    // Un upload en echec ne renvoie pas de fileId : on interrompt pour ne pas enregistrer de document manquant
    if (!responsePostFile?.fileId) return null
    result.push(responsePostFile)
  }

  return result
}

/**
 * Etape 3 POST file
 * @param {*} param0
 */
function* postFile({ file, tags }) {
  
  const token = yield select(selectCurrentToken)

  const formData = new FormData()
  formData.append("name", getStringRemoveSpecialCharacters(file?.name))
  formData.append("tags", JSON.stringify(tags))
  formData.append("file", file)

  try {
    const response = yield call(
      request,
      `${config?.MSF_BACK_URL}/file/upload`,
      postFileCors(formData, {  token })
    )

    if (responseTypeIsError(response)) {
      console.log("Response Error", response)
      yield put(setErrorMessage(response))
    } else {
      return response
    }
  } catch (err) {
    console.log("Catch Err", err)
    if (err?.response?.status === 413) {
      const langageId = yield select(selectLangageID)
      const message = getTranslationString("FILE_SIZE_TOO_LARGE", langageId) || "File size too large"
      yield put(setErrorMessage({ code: "FILE_SIZE_TOO_LARGE", message }))
    } else {
      yield put(setErrorMessage(err))
    }
  }
}

function* updateAffaireSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  console.log("updateAffaireSaga payload => ", payload)

  try {
    const url = `${config?.MSF_BACK_URL}/affaires/${payload?.id}`

    const { body, files, fileField, notification, infos_notif } = payload

    let payloadToSend = { ...body }

    if (payload?.overidePreviousFile && files?.length === 1) {
      const filesLoop = yield call(processLoop, files)
      if (!filesLoop) return
      const documentsNanoid = filesLoop?.map(file => file?.fileId)
      const newFilesIds = documentsNanoid[0]

      payloadToSend = {
        ...body,
        [fileField]: newFilesIds,
      }
    } else if (files?.length) {
      const filesLoop = yield call(processLoop, files)
      if (!filesLoop) return
      const filesIds = filesLoop?.map(file => file?.fileId)
      const newFilesIds = [...(payload?.old_documents || []), ...filesIds]

      payloadToSend = {
        ...body,
        [fileField]: newFilesIds,
      }
    }

    const response = yield call(request, url, patchCors(payloadToSend, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)

    if (responseTypeIsError(response)) return
    yield put(updateAffaireSuccess(response))
    yield put(setSuccessCode("SUCCESS_TO_PATCH_AFFAIRE"))

    if (notification) {
      yield put(postNotificationAction({ notif: notification, infos_notif }))
    }
    return response
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* downloadDocument({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const { blob, response } = yield call(
      requestFile,
      `${config?.MSF_BACK_URL}/file/${payload?.fileId}/download`,
      getCors(tokens, { contentType: "application/octet-stream" })
    )

    if (response.status !== 200) {
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
      return
    }
    saveAs(blob, payload?.fileName)
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* deleteDocumentAffaire({ payload }) {
  console.log("Saga updateSourceWithDeleteDocument payload => ", payload)

  try {
    let payloadToSend = {}

    const documentsId = payload?.old_documents
    // Remove document from list
    const newDocumentsIds = documentsId?.filter(docId => docId !== payload?.documentId)

    payloadToSend = {
      id: payload?.id,
      body: { [payload?.fileField]: newDocumentsIds },
    }

    // const deleteFileResponse = yield call(deleteDocumentSaga, payload?.documentId)

    // if (responseTypeIsError(deleteFileResponse)) return
    const response = yield call(updateAffaireSaga, { payload: payloadToSend })
    console.log("response", response)

    if (responseTypeIsError(response)) {
      console.log("Response Error", response)
      yield call(manageErrorGenerator, { response, showMessageToUser: true })
    } else if (responseTypeIsSuccess(response)) {
      const showMessageToUser = payload?.showMessageToUser || true
      yield call(manageSuccessGenerator, {
        response: response,
        showMessageToUser: showMessageToUser,
      })
    }
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* deleteDocumentSaga(documentId) {
  const token = yield select(selectCurrentToken)

  try {
    const response = yield call(
      etlRequest,
      `${config?.MSF_BACK_URL}/file/${documentId}/delete`,
      deleteCors({ token })
    )

    if (responseTypeIsError(response)) {
      console.log("Response Error", response)
      yield put(setErrorMessage(response))
    }
    return response
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* addContractAndSendForSignatureSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  const { caseId, file, accessToken, signers } = payload || {}
  if (!caseId || !file) {
    yield put(setErrorMessage("Technical error (missing information)"))
    return
  }

  try {
    const responsePostFile = yield call(postFile, file)
    const fileNanoId = responsePostFile?.fileId
    if (!fileNanoId) return

    const payloadToSend = {
      caseId,
      docId: fileNanoId,
      signers,
      accessToken,
    }

    const messageContent = { message: "Document en cours de préparation. Veuillez patienter ... " }
    yield put(setInfoMessage(messageContent))

    const url = `${config?.MSF_BACK_URL}/docusign/sendCaseContractForSignature`
    const response = yield call(request, url, postCors(payloadToSend, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return

    const redirectUrl = response?.body?.docusignUrl
    if (redirectUrl) {
      window.open(redirectUrl, "_blank", "noopener")
      yield put(setSuccessCode("SUCCESS_TO_CREATE_ENVLOPE"))
    } else {
      console.error("Can't find docusignUrl")
      const errorContent = { message: "Impossible de créer le document" }
      yield put(setErrorMessage(errorContent))
    }
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getAffairesSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const queryParams = new URLSearchParams()
    if (Array.isArray(payload?.statut)) {
      payload.statut.forEach(s => queryParams.append('statut', s))
    }
    if (payload?.year) queryParams.set('year', String(payload.year))
    const queryString = queryParams.toString()
    const url = `${config?.MSF_BACK_URL}/affaires${queryString ? `?${queryString}` : ''}`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    if (responseTypeIsError(response)) yield call(handleResp, params)
    yield put(getAffairesSuccess(response))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getAffaireByIdSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }
  const { id } = payload

  try {
    const url = `${config?.MSF_BACK_URL}/affaires/${id}`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getAffaireByIdSuccess(response))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* updateDataAffaireSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }
  const { id, dataAffaireToUpdate, dataAffaireToCreate } = payload

  try {
    const url = `${config?.MSF_BACK_URL}/data-affaires/severalOfAffaire/${id}`
    const payloadToSend = {
      dataAffaireToUpdate: dataAffaireToUpdate || [],
      dataAffaireToCreate: dataAffaireToCreate || [],
    }
    const response = yield call(request, url, patchCors(payloadToSend, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) {
      yield put(setSuccessCode("FAIL_TO_PATCH_DATA_AFFAIRE"))
      return
    }
    yield put(updateDataAffaireSuccess(response?.phaseInfo))
    yield put(setSuccessCode("SUCCESS_TO_PATCH_DATA_AFFAIRE"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
    yield put(setSuccessCode("FAIL_TO_PATCH_DATA_AFFAIRE"))
  }
}

function* getNumberOfAffairesCreatedThisYear() {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/affaires/createdThisYear`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    const nbAffaireCreatedThisYear = response?.nbAffaireCreatedThisYear || 0
    yield put(getNumberOfAffairesCreatedThisYearSuccess(nbAffaireCreatedThisYear))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getLineNameByCodeSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/affaires/line-name/${payload?.code}`
    const response = yield call(request, url, getCors(tokens))
    const lineName = {
      name: response.line_name,
      index: payload?.index,
    }
    yield put(getLineNameByCodeSuccess(lineName))
  } catch (err) {
    console.log("Catch Err", err)
  }
}

function* exportDocumentsZipSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/file/exportZip/${payload?.id}`
    const { blob, response } = yield call(requestFile, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    saveAs(blob, `${payload?.fileName}.zip`)
    if (responseTypeIsError(response)) return
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* exportExcelAffairesSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/data-affaires/export?year=${payload.year}&regions=${
      payload?.regions?.join(",") || ""
    }`
    const { blob, response } = yield call(requestFile, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    saveAs(blob, `${payload?.fileName}.xlsx`)
    if (responseTypeIsError(response)) return
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getWorksPlanningSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/planning-travaux/byAffaire/${payload?.id}`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getWorksPlanningSuccess(response))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* createWorkPlanningSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/planning-travaux`
    const response = yield call(request, url, postCors(payload, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(setSuccessCode("SUCCESS_TO_CREATE_WORKS_PLANNING"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage)
  }
}

function* updateWorkPlanningSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/planning-travaux/${payload?.id}`
    const response = yield call(request, url, patchCors(payload?.body, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(setSuccessCode("SUCCESS_TO_PATCH_WORKS_PLANNING"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* deleteWorkPlanningSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/planning-travaux/${payload?.id}`
    const response = yield call(request, url, deleteCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(setSuccessCode("SUCCESS_TO_DELETE_WORKS_PLANNING"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* validateWorkPlanningSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  const { id, notification, infos_notif } = payload

  try {
    const url = `${config?.MSF_BACK_URL}/planning-travaux/validate/${id}`
    const response = yield call(request, url, patchCors({}, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    yield put(setSuccessCode("SUCCESS_TO_PATCH_WORKS_PLANNING"))
    if (notification) {
      yield put(postNotificationAction({ notif: notification, infos_notif }))
    }
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getCapacitesSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/capacites/byAffaire/${payload?.id}`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getCapacitesSuccess(response))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* createCapaciteSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/capacites`
    const response = yield call(request, url, postCors(payload, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(setSuccessCode("SUCCESS_TO_CREATE_CAPACITES"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* updateCapaciteSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/capacites/${payload?.id}`
    const response = yield call(request, url, patchCors(payload?.body, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(setSuccessCode("SUCCESS_TO_PATCH_CAPACITES"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* deleteCapaciteSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/capacites/${payload?.id}`
    const response = yield call(request, url, deleteCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(setSuccessCode("SUCCESS_TO_DELETE_CAPACITES"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getDocumentsModelsSaga() {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/documents-models`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getModelsSuccess(response))
    yield put(setSuccessCode("SUCCESS_TO_GET_MODELS"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getDocumentsModelsByTypeSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/documents-models/type/${payload?.type}`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getModelsSuccess(response))
    yield put(setSuccessCode("SUCCESS_TO_GET_MODELS"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* createDocumentModelSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }
  const url = `${config?.MSF_BACK_URL}/documents-models`

  try {
    const file = yield call(postFile, payload?.file)
    if (!file?.fileId) return
    const payloadToSend = {
      owner: payload?.owner,
      name: payload?.name,
      type: payload?.type,
      document_id: file.fileId,
    }
    const response = yield call(request, url, postCors(payloadToSend, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(createModelSuccess(response))
    yield put(setSuccessCode("SUCCESS_TO_CREATE_MODEL"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* deleteDocumentModelSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  const { id, document_id } = payload
  const url = `${config?.MSF_BACK_URL}/documents-models/${id}`

  try {
    // const deleteFileResponse = yield call(deleteDocumentSaga, document_id)

    // if (responseTypeIsError(deleteFileResponse)) return
    const response = yield call(request, url, deleteCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(deleteModelSuccess(response))
    yield put(setSuccessCode("SUCCESS_TO_DELETE_MODEL"))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* sendEmailSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/email/${payload?.mailType}`
    const response = yield call(request, url, postCors(payload, tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: true } }
    yield call(handleResp, params)
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getDocuSignAuthUrlSaga() {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/docusign/getAuthUrl`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: false } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getDocuSignAuthUrlSuccess(response?.url))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* getDocuSignAccessTokenSaga({ payload }) {
  const token = yield select(selectCurrentToken)
  const tokens = { token }

  try {
    const url = `${config?.MSF_BACK_URL}/docusign/getAccessToken/${payload?.code}`
    const response = yield call(request, url, getCors(tokens))
    const params = { response, displayMessageToUser: { showError: true, showSuccess: true } }
    yield call(handleResp, params)
    if (responseTypeIsError(response)) return
    yield put(getDocuSignAccessTokenSuccess(response?.accessToken))
  } catch (err) {
    console.log("Catch Err", err)
    yield put(setErrorMessage(err))
  }
}

function* caseSaga() {
  yield takeEvery(CaseTypes.DOWNLOAD_DOCUMENT, downloadDocument)
  yield takeEvery(CaseTypes.DELETE_DOCUMENT_AFFAIRE, deleteDocumentAffaire)
  yield takeEvery(CaseTypes.POST_AFFAIRE, createAffaireSaga)
  yield takeEvery(CaseTypes.PATCH_AFFAIRE, updateAffaireSaga)
  yield takeEvery(CaseTypes.PATCH_DATA_AFFAIRE, updateDataAffaireSaga)
  yield takeEvery(CaseTypes.ADD_AND_SEND_CONTRACT_FOR_SIGNATURE, addContractAndSendForSignatureSaga)
  yield takeEvery(CaseTypes.GET_AFFAIRES, getAffairesSaga)
  yield takeEvery(CaseTypes.GET_AFFAIRE_BY_ID, getAffaireByIdSaga)
  yield takeEvery(CaseTypes.GET_LINE_NAME_BY_CODE, getLineNameByCodeSaga)
  yield takeEvery(
    CaseTypes.GET_NUMBER_OF_AFFAIRES_CREATED_THIS_YEAR,
    getNumberOfAffairesCreatedThisYear
  )
  yield takeEvery(CaseTypes.EXPORT_DOCUMENTS_ZIP, exportDocumentsZipSaga)
  yield takeEvery(CaseTypes.EXPORT_EXCEL, exportExcelAffairesSaga)
  yield takeEvery(CaseTypes.GET_WORKS_PLANNING, getWorksPlanningSaga)
  yield takeEvery(CaseTypes.POST_WORKS_PLANNING, createWorkPlanningSaga)
  yield takeEvery(CaseTypes.PATCH_WORKS_PLANNING, updateWorkPlanningSaga)
  yield takeEvery(CaseTypes.DELETE_WORKS_PLANNING, deleteWorkPlanningSaga)
  yield takeEvery(CaseTypes.VALIDATE_WORKS_PLANNING, validateWorkPlanningSaga)
  yield takeEvery(CaseTypes.GET_CAPACITES, getCapacitesSaga)
  yield takeEvery(CaseTypes.POST_CAPACITES, createCapaciteSaga)
  yield takeEvery(CaseTypes.PATCH_CAPACITES, updateCapaciteSaga)
  yield takeEvery(CaseTypes.DELETE_CAPACITES, deleteCapaciteSaga)
  yield takeEvery(CaseTypes.GET_MODELS, getDocumentsModelsSaga)
  yield takeEvery(CaseTypes.GET_MODELS_BY_TYPE, getDocumentsModelsByTypeSaga)
  yield takeEvery(CaseTypes.POST_MODEL, createDocumentModelSaga)
  yield takeEvery(CaseTypes.DELETE_MODEL, deleteDocumentModelSaga)
  yield takeEvery(CaseTypes.MSF_SEND_EMAIL, sendEmailSaga)
  yield takeEvery(DocuSignTypes.GET_DOCUSIGN_AUTH_URL, getDocuSignAuthUrlSaga)
  yield takeEvery(DocuSignTypes.GET_DOCUSIGN_ACCESS_TOKEN, getDocuSignAccessTokenSaga)
}

export default caseSaga
